// src/components/BGP/BgpPage.tsx
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
    columns as baseColumns,
    BgpNeighbor,
} from "./BgpData";

import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";

// Filtro
import { GenericFilter } from "@ui-components/Filter/GenericFilter";

import {
    getCTXRlistGlobal,
    getBgpNeighborsByCtxr,
    getLocalAddressesByCtxrId,
    updateBgpNeighbor,
    createBgpNeighbor,
    deleteBgpNeighbor,
} from "./request/request";

import { ReactComponent as IconEdit } from "../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../shared/assets/Icons/Icon_Delete.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";

interface CtxrItem {
    id: string;
    name: string;
    description: string;
    subscriptionId: number;
    Suscription?: string;
}

function BgpPage() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

    // Overlay spinner
    const [isFetching, setIsFetching] = useState(false);

    // CTX-R list
    const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
    const [instanceOptions, setInstanceOptions] = useState<string[]>([]);
    const [selectedInstance, setSelectedInstance] = useState("");

    // BGP neighbors
    const [neighbors, setNeighbors] = useState<BgpNeighbor[]>([]);

    // cni dropdown
    const [cniOptions, setCniOptions] = useState<string[]>([]);

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);

    // Form
    const [neighborId, setNeighborId] = useState<number | null>(null);
    const [neighborIP, setNeighborIP] = useState("");
    const [routerID, setRouterID] = useState("");
    const [localAS, setLocalAS] = useState("");
    const [peerAS, setPeerAS] = useState("");
    const [holdTime, setHoldTime] = useState("3");
    const [description, setDescription] = useState("");
    const [localAddress, setLocalAddress] = useState("Select a IP");
    const [vpn4, setVpn4] = useState(false);
    const [ipv4Unicast, setIpv4Unicast] = useState(false);

    // Para deshabilitar el botón mientras se realiza la petición de Guardar
    const [isSaving, setIsSaving] = useState(false);

    // SEARCH
    const [searchTerm, setSearchTerm] = useState("");
    const [searchColumn, setSearchColumn] = useState("All");
    const searchOptions = [
        "All",
        "Neighbor IP",
        "Local address",
        "Local AS",
        "Peer AS",
        "Router ID",
    ];

    const filteredNeighbors = neighbors.filter((row) => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;
        const includes = (val: string) => val.toLowerCase().includes(term);

        switch (searchColumn) {
            case "Neighbor IP":
                return includes(row.ipid);
            case "Local address":
                return includes(row.local_address ?? "");
            case "Local AS":
                return includes(String(row.local_as ?? ""));
            case "Peer AS":
                return includes(String(row.peer_as ?? ""));
            case "Router ID":
                return includes(row.router_id);
            case "All":
            default:
                return (
                    includes(row.ipid) ||
                    includes(row.local_address ?? "") ||
                    includes(String(row.local_as ?? "")) ||
                    includes(String(row.peer_as ?? "")) ||
                    includes(row.router_id)
                );
        }
    });

    // DELETE flow
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<BgpNeighbor | null>(null);

    // Reutilizamos el RESULT ALERT para delete y ahora también para create/update
    const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
    const [alertVariant, setAlertVariant] = useState<"success" | "error">("success");
    const [alertTitle, setAlertTitle] = useState("");
    const [alertDescription, setAlertDescription] = useState("");

    // 1) Cargar CTX-R
    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;

        const fetchCTXRList = async () => {
            try {
                setIsFetching(true);
                const token = await getAccessTokenSilently();
                const response = await getCTXRlistGlobal(-1, 1, token);
                const dataArray: CtxrItem[] = response.data || [];

                setFullCTXRlist(dataArray);
                const mapped = dataArray.map(
                    (item) => `${item.name} (${item.description})`
                );
                setInstanceOptions(mapped);

                if (mapped.length > 0) {
                    const first = mapped[0];
                    setSelectedInstance(first);

                    const found = dataArray.find(
                        (obj) => `${obj.name} (${obj.description})` === first
                    );
                    if (found) {
                        await loadBgpNeighbors(found.id);
                    }
                }
            } catch (err) {
                console.error("Error fetching CTXR list:", err);
            } finally {
                setIsFetching(false);
            }
        };

        fetchCTXRList();
    }, [isLoading, isAuthenticated, getAccessTokenSilently]);

    // 2) Cambiar instancia => neighbors
    const handleInstanceSelect = async (selected: string) => {
        setSelectedInstance(selected);
        const foundObj = fullCTXRlist.find(
            (obj) => `${obj.name} (${obj.description})` === selected
        );
        if (!foundObj) return;
        await loadBgpNeighbors(foundObj.id);
    };

    // load neighbors
    const loadBgpNeighbors = async (ctxrId: string) => {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await getBgpNeighborsByCtxr(token, ctxrId);
            setNeighbors(resp.data);
        } catch (error) {
            console.error("Error fetching BGP neighbors =>", error);
        } finally {
            setIsFetching(false);
        }
    };

    // Refresh
    const handleRefresh = async () => {
        const foundObj = fullCTXRlist.find(
            (obj) => `${obj.name} (${obj.description})` === selectedInstance
        );
        if (!foundObj) return;
        await loadBgpNeighbors(foundObj.id);
    };

    // Add BGP => form vacío
    const handleAddBGP = async () => {
        setEditMode(false);
        setNeighborId(null);
        setNeighborIP("");
        setRouterID("");
        setLocalAS("");
        setPeerAS("");
        setHoldTime("3");
        setDescription("");
        setLocalAddress("Select a IP");
        setVpn4(false);
        setIpv4Unicast(false);

        try {
            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === selectedInstance
            );
            if (foundObj) {
                await loadLocalAddresses(foundObj.id);
            }
        } catch (err) {
            console.error("Error loading cni for Add =>", err);
        }

        setIsModalOpen(true);
    };

    // Edit BGP => form con data
    const handleEditBGP = async (rowData: BgpNeighbor) => {
        try {
            setEditMode(true);

            setNeighborId(rowData.id);
            setNeighborIP(rowData.ipid);
            setRouterID(rowData.router_id);
            setLocalAS(String(rowData.local_as));
            setPeerAS(String(rowData.peer_as));
            setHoldTime(String(rowData.holdtime));
            setDescription(rowData.description ?? "");
            setLocalAddress(rowData.local_address);

            const families = (rowData.bgpfamily || "").toLowerCase();
            setVpn4(families.includes("mpls-vpn"));
            setIpv4Unicast(families.includes("unicast"));

            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === selectedInstance
            );
            if (foundObj) {
                await loadLocalAddresses(foundObj.id);
            }

            setIsModalOpen(true);
        } catch (error) {
            console.error("Error in handleEditBGP =>", error);
        }
    };

    // load cni
    const loadLocalAddresses = async (ctxrId: string) => {
        setIsFetching(true);
        try {
            const token = await getAccessTokenSilently();
            const resp = await getLocalAddressesByCtxrId(token, ctxrId);
            const items = resp.data.map(
                (item: { name: string; value: string }) => item.value
            );
            setCniOptions(items);
        } catch (err) {
            console.error("Error loading local addresses =>", err);
        } finally {
            setIsFetching(false);
        }
    };

    // isFormValid
    const isFormValid = Boolean(
        neighborIP.trim() &&
        routerID.trim() &&
        localAddress !== "Select a IP" &&
        localAS.trim() &&
        peerAS.trim() &&
        holdTime.trim() &&
        (vpn4 || ipv4Unicast)
    );

    // 6) Save BGP => success/error alert
    const handleSaveNeighbor = async () => {
        // Al hacer clic en Save, deshabilitamos el botón
        setIsSaving(true);

        try {
            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === selectedInstance
            );
            if (!foundObj) {
                console.warn("No CTXR found for:", selectedInstance);
                setIsModalOpen(false);
                return;
            }
            const ctxrId = foundObj.id;

            const token = await getAccessTokenSilently();
            const families: string[] = [];
            if (vpn4) families.push("ipv4 mpls-vpn");
            if (ipv4Unicast) families.push("ipv4 unicast");

            if (editMode && neighborId != null) {
                // Update
                await updateBgpNeighbor(token, ctxrId, neighborId, {
                    ipid: neighborIP,
                    router_id: routerID,
                    local_address: localAddress,
                    local_as: Number(localAS) || 0,
                    peer_as: Number(peerAS) || 0,
                    bgpfamily: families,
                    holdtime: holdTime,
                    description: description,
                });

                // Tras actualizar => alert success
                setAlertVariant("success");
                setAlertTitle("Neighbor updated successfully!");
                setAlertDescription(`Neighbor "${neighborIP}" was updated.`);
            } else {
                // Create
                await createBgpNeighbor(token, ctxrId, {
                    ipid: neighborIP,
                    router_id: routerID,
                    local_address: localAddress,
                    local_as: Number(localAS) || 0,
                    peer_as: Number(peerAS) || 0,
                    typeConfig: 0,
                    bgpfamily: families,
                    holdtime: Number(holdTime) || 3,
                    bgppassword: "",
                    description: description,
                });

                // Tras crear => alert success
                setAlertVariant("success");
                setAlertTitle("Neighbor created successfully!");
                setAlertDescription(`Neighbor "${neighborIP}" was created.`);
            }

            await loadBgpNeighbors(ctxrId);
        } catch (error) {
            console.error("Error saving BGP neighbor =>", error);
            // Manejamos error => alert
            setAlertVariant("error");
            setAlertTitle("Error saving neighbor");
            setAlertDescription(
                "An error occurred while saving. Please try again."
            );
        } finally {
            // Rehabilitamos el botón y cerramos el modal
            setIsSaving(false);
            setIsModalOpen(false);
            // Mostramos la alerta final (success / error)
            setIsResultAlertOpen(true);
        }
    };

    // 7) Delete
    const handleDeleteClick = (rowData: BgpNeighbor) => {
        setRowToDelete(rowData);
        setIsAlertOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!rowToDelete) return;
        setIsAlertOpen(false);

        try {
            setIsFetching(true);
            const foundObj = fullCTXRlist.find(
                (obj) => `${obj.name} (${obj.description})` === selectedInstance
            );
            if (!foundObj) {
                console.warn("No CTXR found for:", selectedInstance);
                return;
            }
            const ctxrId = foundObj.id;

            const token = await getAccessTokenSilently();
            await deleteBgpNeighbor(token, ctxrId, rowToDelete.id);

            // recargar
            await loadBgpNeighbors(ctxrId);

            // => success
            setAlertVariant("success");
            setAlertTitle("Removed successfully!");
            setAlertDescription(`Neighbor "${rowToDelete.ipid}" was deleted.`);
        } catch (err) {
            console.error("Error deleting neighbor =>", err);
            setAlertVariant("error");
            setAlertTitle("Could not remove neighbor");
            setAlertDescription(
                `An error occurred removing "${rowToDelete.ipid}". Please try again.`
            );
        } finally {
            setIsFetching(false);
            setRowToDelete(null);
            setIsResultAlertOpen(true);
        }
    };

    const handleCancelDelete = () => {
        setIsAlertOpen(false);
        setRowToDelete(null);
    };

    // 8) Overwrite "Actions" col
    const finalColumns = baseColumns.map((col) => {
        if (col.header === "Actions" && col.renderCell) {
            return {
                ...col,
                renderCell: (row: BgpNeighbor) => (
                    <div className="flex items-center gap-2">
                        <button
                            className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10 flex items-center justify-center
              "
                            onClick={() => handleEditBGP(row)}
                        >
                            <IconEdit className="w-6 h-6" />
                        </button>

                        <button
                            className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-danger-secondary
                text-semantic-fg-danger
                hover:bg-red-100
                w-10 h-10 flex items-center justify-center
              "
                            onClick={() => handleDeleteClick(row)}
                        >
                            <IconDelete className="w-5 h-5" />
                        </button>
                    </div>
                ),
            };
        }
        return col;
    });

    return (
        <div className="relative">
            {/* Header */}
            <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
                <div className="flex items-end justify-between">
                    <div className="flex items-end space-x-4">
                        {/* CTX-R instance + Refresh */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                                Select a CTX-R instance
                            </label>
                            <GenericDropdown
                                items={instanceOptions}
                                defaultLabel=""
                                value={selectedInstance}
                                onSelect={handleInstanceSelect}
                                minWidthClass="min-w-[300px]"
                                widthClass="w-60"
                            />
                        </div>

                        <GenericButton
                            label="Refresh"
                            variant="secondary"
                            shape="ghost"
                            size="md"
                            onClick={handleRefresh}
                        />
                    </div>

                    {/* Add BGP neighbor */}
                    <div className="flex items-center space-x-4">
                        <GenericButton
                            label="Add BGP neighbour"
                            variant="brand"
                            size="md"
                            onClick={handleAddBGP}
                        />
                    </div>
                </div>
            </div>

            {/* Row con el Filter */}
            <div className="mb-4 bg-[var(--semantic-bg-primary)]">
                <div className="flex items-end justify-between">
                    <div className="flex items-end space-x-4" />
                    <div className="flex items-center space-x-4">
                        <GenericFilter
                            searchColumn={searchColumn}
                            onSearchColumnChange={setSearchColumn}
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            columnOptions={searchOptions}
                        />
                    </div>
                </div>
            </div>

            {/* Table con data filtrada */}
            <GenericTable
                columns={finalColumns}
                data={filteredNeighbors}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
            />

            {/* Modal Add/Edit */}
            <GenericModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editMode ? "Edit BGP Neighbor" : "Add BGP Neighbor"}
                size="lg"
            >
                {/* CTX-R solo si estamos en modo Add */}
                {!editMode && (
                    <div className="mb-4 flex flex-col items-center justify-center text-center">
                        <p className="text-sm font-medium text-semantic-text-primary">
                            Selected CTX-R Instance *
                        </p>
                        <p className="text-sm text-semantic-text-secondary mb-2">
                            {selectedInstance || "No instance selected"}
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Neighbor IP <span className="text-red-500">*</span>
                        </label>
                        <GenericInput
                            placeholder="e.g. 10.100.0.8"
                            value={neighborIP}
                            onChange={setNeighborIP}
                        />
                    </div>

                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Router ID <span className="text-red-500">*</span>
                        </label>
                        <GenericInput
                            placeholder="e.g. 1.2.3.4"
                            value={routerID}
                            onChange={setRouterID}
                        />
                    </div>

                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Local Address <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={cniOptions.length > 0 ? cniOptions : ["Select a IP"]}
                            value={localAddress}
                            onSelect={setLocalAddress}
                            widthClass="w-full"
                            maxWidthClass="100%"
                        />
                    </div>

                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Local AS <span className="text-red-500">*</span>
                        </label>
                        <GenericInput
                            placeholder="e.g. 65117"
                            value={localAS}
                            onChange={setLocalAS}
                        />
                    </div>

                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Peer AS <span className="text-red-500">*</span>
                        </label>
                        <GenericInput
                            placeholder="e.g. 65118"
                            value={peerAS}
                            onChange={setPeerAS}
                        />
                    </div>

                    <div>
                        <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                            Hold Time <span className="text-red-500">*</span>
                        </label>
                        <GenericInput
                            placeholder="3"
                            value={holdTime}
                            onChange={setHoldTime}
                        />
                    </div>

                    <div className="col-span-3">
                        <GenericInput
                            label="Description"
                            placeholder="(optional)"
                            value={description}
                            onChange={setDescription}
                        />
                    </div>
                </div>

                {/* BGP Families */}
                <div className="mb-4">
                    <p className="text-sm font-medium text-semantic-text-primary mb-1 flex items-center gap-1">
                        BGP Families <span className="text-red-500">*</span>
                        <GenericTooltip text="This field only required at least 1 option" />
                    </p>
                    <div className="flex space-x-6">
                        <label className="flex items-center space-x-1 text-sm text-semantic-text-primary">
                            <input
                                type="checkbox"
                                checked={vpn4}
                                onChange={(e) => setVpn4(e.target.checked)}
                            />
                            <span>VPNv4</span>
                        </label>
                        <label className="flex items-center space-x-1 text-sm text-semantic-text-primary">
                            <input
                                type="checkbox"
                                checked={ipv4Unicast}
                                onChange={(e) => setIpv4Unicast(e.target.checked)}
                            />
                            <span>IPv4 Unicast</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end space-x-2">
                    <button
                        className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-primary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
                        onClick={() => setIsModalOpen(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        className={`
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              transition-colors
              ${isFormValid && !isSaving
                                ? "hover:bg-opacity-90 cursor-pointer"
                                : "opacity-50 cursor-not-allowed"
                            }
            `}
                        onClick={handleSaveNeighbor}
                        disabled={!isFormValid || isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </div>
            </GenericModal>

            {/* Delete confirm */}
            <GenericAlert
                isOpen={isAlertOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Are you sure you want to delete this item?"
                description={
                    rowToDelete
                        ? `Deleting "${rowToDelete.ipid}" cannot be undone.`
                        : undefined
                }
                cancelLabel="Cancel"
                confirmLabel="Delete"
                variant="confirm"
            />

            {/* Result alert: success/error for both DELETE and CREATE/UPDATE */}
            <GenericAlert
                isOpen={isResultAlertOpen}
                onClose={() => setIsResultAlertOpen(false)}
                onConfirm={() => setIsResultAlertOpen(false)}
                title={alertTitle}
                description={alertDescription}
                cancelLabel="Close"
                confirmLabel="OK"
                variant={alertVariant}
            />

            {/* Overlay spinner */}
            {isFetching && (
                <div
                    className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
                >
                    <h2 className="text-xl font-semibold mb-4 text-white">
                        Processing request...
                    </h2>
                    <div
                        className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4 border-gray-300
              border-t-blue-500
            "
                    />
                </div>
            )}
        </div>
    );
}

export default BgpPage;
